/**
 * Telling a directory request that was called off apart from one that failed.
 *
 * Kept free of Vue and of the HTTP client so it stays testable with Vitest,
 * like the rest of `@c/addressBook`.
 */

/**
 * The code the HTTP client (`src/utils/http/createHttpClient.ts`) sets on the
 * error it rejects with when a request is aborted through its `signal`.
 *
 * Already the literal the HTTP client itself tests before deciding whether to
 * toast — `if (error.code === 'ERR_CANCELED') return Promise.reject(error)`
 * (`handleResponseError` in `apiClient.js`) — and the one every other screen that aborts in flight
 * tests too (`InstantSearchPalette.vue:373`, `ThirdPartyCostContent.vue:309`,
 * `ThreadListComponent.vue:174`). Named here so the address book does not spell
 * it a fifth time, and so the one place that has to recognise a cancellation
 * cannot drift from the one place that has to stay silent about it.
 *
 * @type {string}
 */
export const CANCELED_REQUEST_CODE = 'ERR_CANCELED'

/**
 * Whether a rejection is a request we called off ourselves.
 *
 * The distinction is the whole point: a cancellation is an outcome we asked
 * for, so it must leave no toast and no trace, while a genuine failure has to
 * keep behaving exactly as it did before — the interceptor toasts it, and the
 * caller clears its loading flag.
 *
 * Reads the code defensively because a rejection is not guaranteed to be an
 * axios error at all: a `TypeError` thrown inside a `then` handler arrives on
 * the same channel, and must never be mistaken for a cancellation and silently
 * swallowed.
 *
 * @param {{code?: string}|Error|null|undefined} reason
 * @returns {boolean}
 */
export function isCanceledRequest(reason) {
  return reason?.code === CANCELED_REQUEST_CODE
}
